import { useState, useEffect, useMemo } from 'react';
import { Users, Plus, Search, Shield, Check, X, Edit, Trash2, Loader2 } from 'lucide-react';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import api from '../api/client';

// Roles selectable from this form: just Owner and User, each with a single
// Read/Write switch (the Access Level field below) — no granular permission
// checklist. "Portal Admin"/"admin" is no longer offered here. Stored role
// values are deliberately NOT "owner"/"user": "owner" already means
// something else entirely (café-side, aliases "cafeadmin") elsewhere in the
// backend, so these use "platform_owner"/"platform_user" to avoid colliding
// with that — purely an internal detail, invisible here via ROLE_LABELS.
// "admin"/"superadmin" labels are kept so any such row (e.g. the fixed Super
// Admin accounts) still displays correctly in the table below.
const ROLE_LABELS = { admin: 'Portal Admin', superadmin: 'Super Admin', platform_owner: 'Owner', platform_user: 'User' };
const ACCESS_ROLES = ['platform_owner', 'platform_user'];

const EMPTY_FORM = { name: '', email: '', password: '', role: 'platform_owner', is_email_verified: true, access_level: null };

export default function UserManagement() {
    const { isSuperAdmin, hasPermission, user: currentUser } = useAuthStore();
    const canManageUsers = isSuperAdmin() || hasPermission(PERMISSIONS.MANAGE_USERS);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const fetchUsers = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get('/internal/users');
            const transformed = (response.data || []).map(u => ({
                id: u.id,
                username: u.name || u.email.split('@')[0],
                email: u.email,
                role: u.role,
                status: u.is_email_verified ? 'active' : 'inactive',
                permissions: u.permissions || [],
                accessLevel: u.access_level || null,
            }));
            setUsers(transformed);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            setError(err.response?.data?.detail || 'Failed to load users');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const filtered = useMemo(() => {
        if (!searchQuery) return users;
        const q = searchQuery.toLowerCase();
        return users.filter(u => u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    }, [users, searchQuery]);

    const openCreateModal = () => {
        setEditingUser(null);
        setForm(EMPTY_FORM);
        setFormError('');
        setShowModal(true);
    };
    const openEditModal = (user) => {
        setEditingUser(user);
        setForm({
            name: user.username, email: user.email, password: '', role: user.role,
            is_email_verified: user.status === 'active', access_level: user.accessLevel,
        });
        setFormError('');
        setShowModal(true);
    };

    const save = async () => {
        setFormError('');
        if (!form.name.trim() || !form.email.trim()) {
            setFormError('Name and email are required.');
            return;
        }
        if (!form.access_level) {
            setFormError('Choose Read or Write access for this user.');
            return;
        }
        setSaving(true);
        try {
            if (editingUser) {
                await api.patch(`/internal/users/${editingUser.id}`, {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    role: form.role,
                    is_email_verified: form.is_email_verified,
                    access_level: form.access_level,
                });
            } else {
                if (!form.password || form.password.length < 8) {
                    setFormError('Password must be at least 8 characters.');
                    setSaving(false);
                    return;
                }
                await api.post('/internal/users', {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    password: form.password,
                    role: form.role,
                    access_level: form.access_level,
                });
            }
            setShowModal(false);
            await fetchUsers();
        } catch (err) {
            console.error('Failed to save user:', err);
            setFormError(err.response?.data?.detail || 'Failed to save user.');
        } finally {
            setSaving(false);
        }
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`Delete ${user.username}? This cannot be undone from here.`)) return;
        setDeletingId(user.id);
        try {
            await api.delete(`/internal/users/${user.id}`);
            await fetchUsers();
        } catch (err) {
            console.error('Failed to delete user:', err);
            alert(err.response?.data?.detail || 'Failed to delete user.');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="user-management">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Users & Roles</h1>
                    <p className="page-subtitle">Manage internal users and permissions</p>
                </div>
                <button
                    className="btn btn--primary"
                    onClick={openCreateModal}
                    disabled={!canManageUsers}
                    title={!canManageUsers ? 'Read-only access' : undefined}
                >
                    <Plus size={16} /> Add User
                </button>
            </div>

            {/* Stats */}
            <div className="stats-row">
                <div className="mini-stat glass-card"><Users size={20} /><span>{users.length}</span><span>Total Users</span></div>
                <div className="mini-stat glass-card"><Shield size={20} /><span>{users.filter(u => u.role === 'superadmin').length}</span><span>Super Admins</span></div>
                <div className="mini-stat glass-card"><Check size={20} /><span>{users.filter(u => u.status === 'active').length}</span><span>Active</span></div>
            </div>

            {/* Search */}
            <div className="search-bar">
                <Search size={16} className="search-bar__icon" />
                <input type="text" placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-bar__input" />
            </div>

            {/* Table */}
            <div className="card table-card">
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th>Permissions</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={5} className="text-tertiary">Loading…</td></tr>
                        ) : error ? (
                            <tr><td colSpan={5} className="text-tertiary">{error}</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={5} className="text-tertiary">No users found.</td></tr>
                        ) : filtered.map(user => (
                            <tr key={user.id}>
                                <td>
                                    <div className="user-cell">
                                        <div className="user-cell__avatar">{user.username.charAt(0).toUpperCase()}</div>
                                        <div className="user-cell__info">
                                            <span className="user-cell__name">{user.username}</span>
                                            <span className="user-cell__email">{user.email}</span>
                                        </div>
                                    </div>
                                </td>
                                <td><span className={`badge ${user.role === 'superadmin' ? 'badge--primary' : 'badge--neutral'}`}>{ROLE_LABELS[user.role] || user.role}</span></td>
                                <td>
                                    <span className="text-secondary">
                                        {ACCESS_ROLES.includes(user.role)
                                            ? (user.accessLevel === 'write' ? 'Write access' : 'Read-only')
                                            : `${user.permissions.length} permissions`}
                                    </span>
                                </td>
                                <td><span className={`badge badge--${user.status === 'active' ? 'success' : 'neutral'}`}>{user.status}</span></td>
                                <td>
                                    <div className="row-actions">
                                        <button className="btn btn--icon btn--ghost" onClick={() => openEditModal(user)}><Edit size={16} /></button>
                                        {user.role !== 'superadmin' && user.id !== currentUser?.id && (
                                            <button className="btn btn--icon btn--ghost" disabled={deletingId === user.id} onClick={() => deleteUser(user)}>
                                                {deletingId === user.id ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />}
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-backdrop" onClick={() => setShowModal(false)}>
                    <div className="modal modal--lg" onClick={e => e.stopPropagation()}>
                        <div className="modal__header">
                            <h2 className="modal__title">{editingUser ? 'Edit User' : 'Create User'}</h2>
                            <button className="modal__close" onClick={() => setShowModal(false)}><X size={20} /></button>
                        </div>
                        <div className="modal__body">
                            {formError && (
                                <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--status-danger-subtle)', color: 'var(--status-danger)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)' }}>
                                    {formError}
                                </div>
                            )}
                            <div className="form-grid">
                                <div className="input-group">
                                    <label className="input-label">Name</label>
                                    <input type="text" className="input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Email</label>
                                    <input type="email" className="input" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
                                </div>
                                {!editingUser && (
                                    <div className="input-group">
                                        <label className="input-label">Password</label>
                                        <input type="password" className="input" placeholder="Min 8 characters" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} />
                                    </div>
                                )}
                                <div className="input-group">
                                    <label className="input-label">Role</label>
                                    <select className="input select" value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}>
                                        <option value="platform_owner">{ROLE_LABELS.platform_owner}</option>
                                        <option value="platform_user">{ROLE_LABELS.platform_user}</option>
                                    </select>
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Status</label>
                                    <select
                                        className="input select"
                                        value={form.is_email_verified ? 'active' : 'inactive'}
                                        onChange={(e) => setForm(f => ({ ...f, is_email_verified: e.target.value === 'active' }))}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                                <div className="input-group input-group--wide">
                                    <label className="input-label">Access Level</label>
                                    <div className="access-level-choice">
                                        <label className="access-level-option">
                                            <input
                                                type="radio"
                                                name="access_level"
                                                checked={form.access_level === 'read'}
                                                onChange={() => setForm(f => ({ ...f, access_level: 'read' }))}
                                            />
                                            <span>
                                                <strong>Read</strong>
                                                <small>Can view every page. Add/create buttons are disabled.</small>
                                            </span>
                                        </label>
                                        <label className="access-level-option">
                                            <input
                                                type="radio"
                                                name="access_level"
                                                checked={form.access_level === 'write'}
                                                onChange={() => setForm(f => ({ ...f, access_level: 'write' }))}
                                            />
                                            <span>
                                                <strong>Write</strong>
                                                <small>Full access — can view and add/create on every page.</small>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal__footer">
                            <button className="btn btn--ghost" onClick={() => setShowModal(false)}>Cancel</button>
                            <button className="btn btn--primary" onClick={save} disabled={saving}>
                                {saving ? <Loader2 size={16} className="spin" /> : (editingUser ? 'Save Changes' : 'Create User')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        .user-management {
          /* noLag rebrand: scoped to this page only. Confirmed via
             get_design_context on 2559:116 ("Users & Roles") — title and
             subtitle match this page exactly, so this IS the referenced
             node (the table's row content in Figma is generic mock data
             reused from another template, not meaningful here).
             badge--primary (superadmin role) flows to orange automatically
             — that's a role emphasis, not a status color; badge--success/
             neutral (Active/Inactive) are untouched separate tokens. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1100px;
        }
        /* Figma-confirmed white-sheen recipe (2561:546 etc.) for the 3
           mini-stat cards — same recipe as every other page's stat cards.
           Not nested, so its own backdrop-filter is safe. */
        .user-management .glass-card {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
        }
        .user-management .glass-card::before { display: none; }
        .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: var(--space-6); }
        .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }

        .stats-row { display: flex; gap: var(--space-4); margin-bottom: var(--space-6); }
        .mini-stat {
          display: flex; align-items: center; gap: var(--space-3); padding: var(--space-4) var(--space-5);
        }
        .mini-stat svg { color: var(--text-tertiary); }
        .mini-stat span:nth-child(2) { font-size: var(--text-xl); font-weight: 600; }
        .mini-stat span:nth-child(3) { font-size: var(--text-sm); color: var(--text-tertiary); }

        .search-bar { position: relative; margin-bottom: var(--space-6); max-width: 320px; }
        .search-bar__icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--text-tertiary); }
        .search-bar__input {
          width: 100%; padding: 12px 14px 12px 40px; font-size: var(--text-sm);
          background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: var(--radius-md); color: var(--text-primary);
        }
        .search-bar__input::placeholder { color: var(--text-quaternary); }
        .search-bar__input:focus { outline: none; border-color: var(--accent-primary); box-shadow: var(--shadow-focus); }

        .table-card { padding: 0; overflow: hidden; }

        .user-cell { display: flex; align-items: center; gap: var(--space-3); }
        .user-cell__avatar {
          width: 36px; height: 36px; background: var(--accent-gradient); border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: var(--text-sm); color: white;
        }
        .user-cell__name { display: block; font-weight: 500; }
        .user-cell__email { display: block; font-size: var(--text-xs); color: var(--text-tertiary); }

        .row-actions { display: flex; gap: var(--space-1); }

        .modal--lg { width: 600px; }
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--space-5); margin-bottom: var(--space-6); }
        .input-group--wide { grid-column: 1 / -1; }

        .access-level-choice { display: flex; flex-direction: column; gap: var(--space-2); }
        .access-level-option {
          display: flex; align-items: flex-start; gap: var(--space-3); padding: var(--space-3);
          background: rgba(0,0,0,0.2); border: 1px solid var(--glass-border); border-radius: var(--radius-md);
          cursor: pointer; transition: border-color var(--duration-fast) var(--ease-out);
        }
        .access-level-option:has(input:checked) { border-color: var(--accent-primary); }
        .access-level-option input { margin-top: 3px; accent-color: var(--accent-primary); }
        .access-level-option span { display: flex; flex-direction: column; gap: 2px; }
        .access-level-option strong { font-size: var(--text-sm); color: var(--text-primary); }
        .access-level-option small { font-size: var(--text-xs); color: var(--text-tertiary); }
      `}</style>
        </div>
    );
}
